import { z } from "zod";
import { mockSizeSchema } from "./progress";
import { itemResponseSchema } from "./scoring";
import { questionSchema } from "./study";

/** Progress and mock-exam contracts (spec §7.2 Progress, Mock Exams; §10.5). */

const range = z.object({ low: z.number(), high: z.number() });
const cohort = z.object({ percentile: z.number().int().min(0).max(100), cohort_size: z.number().int() }).nullable();

export const progressSummarySchema = z.object({
  xp: z.number().int(),
  level: z.number().int(),
  level_name: z.string(),
  level_floor_xp: z.number().int(),
  next_level_xp: z.number().int(),
  streak: z.object({ current: z.number().int(), best: z.number().int(), freezes: z.number().int(), active_today: z.boolean() }),
  badges: z.array(z.object({ key: z.string(), name: z.string(), detail: z.string(), earned_at: z.string() })),
  readiness: z.object({ range: range.nullable(), answered: z.number().int(), needed: z.number().int() }),
  due_cards: z.number().int(),
  cohort_percentile: cohort,
  coach_line: z.string(),
  exam: z.object({ name: z.string(), date: z.string().nullable(), days_left: z.number().int().nullable() }).nullable(),
});
export type ProgressSummary = z.infer<typeof progressSummarySchema>;

export const weeklyReportSchema = z.object({
  days: z.array(z.object({ date: z.string(), minutes: z.number().int(), sessions: z.number().int() })),
  minutes: z.number().int(),
  sessions: z.number().int(),
  questions: z.number().int(),
  accuracy_percent: z.number().int().nullable(),
  cards_reviewed: z.number().int(),
  coach_line: z.string(),
  coach_is_ai: z.boolean(),
  cohort_percentile: cohort,
  first_week: z.boolean(),
});
export type WeeklyReport = z.infer<typeof weeklyReportSchema>;

export const forecastSchema = z.object({
  exam_name: z.string(),
  exam_date: z.string().nullable(),
  days_left: z.number().int().nullable(),
  dimension_name: z.string(),
  weight_unit: z.enum(["percent", "items"]),
  categories: z.array(z.object({ key: z.string(), name: z.string(), weight: z.number(), value: z.number().nullable(), answered: z.number().int() })),
  readiness: range.nullable(),
  projected: z.object({ low: z.number(), high: z.number(), basis: z.string() }).nullable(),
  answered: z.number().int(),
  needed: z.number().int(),
  format_note: z.string(),
});
export type Forecast = z.infer<typeof forecastSchema>;

export const activitySchema = z.object({
  streak: z.object({ current: z.number().int(), best: z.number().int(), freezes: z.number().int() }),
  active_dates: z.array(z.string()),
  sessions: z.array(
    z.object({
      id: z.string().uuid(),
      set_title: z.string().nullable(),
      mode: z.string(),
      answered: z.number().int(),
      score_percent: z.number().int().nullable(),
      duration_seconds: z.number().int(),
      completed_at: z.string(),
    }),
  ),
});
export type Activity = z.infer<typeof activitySchema>;

export const studyPlanSchema = z.object({
  kind: z.enum(["basic", "ai"]),
  exam_date: z.string().nullable(),
  days_left: z.number().int().nullable(),
  items: z.array(z.object({ date: z.string(), title: z.string(), detail: z.string() })),
  generated_at: z.string(),
});
export type StudyPlan = z.infer<typeof studyPlanSchema>;
/** AI pacing plan output (Pro): next 7 days. */
export const aiPlanOutputSchema = z.object({ items: z.array(z.object({ date: z.string(), title: z.string().max(80), detail: z.string().max(240) })).min(1).max(14) });
/** AI coach line / fix-list output (Pro). */
export const coachOutputSchema = z.object({ line: z.string().max(240), fixes: z.array(z.string().max(200)).max(5) });

// ---------------------------------------------------------------- mock exams
export const createMockRequestSchema = z.object({ size: mockSizeSchema, mode: z.enum(["fixed", "adaptive"]).default("fixed") });
export type CreateMockRequest = z.infer<typeof createMockRequestSchema>;

export const mockSchema = z.object({
  id: z.string().uuid(),
  size: mockSizeSchema,
  mode: z.enum(["fixed", "adaptive"]),
  exam_name: z.string(),
  timed_seconds: z.number().int().nullable(),
  started_at: z.string(),
  submitted_at: z.string().nullable(),
  sections: z.array(z.object({ name: z.string(), start: z.number().int(), count: z.number().int(), minutes: z.number().int().nullable() })),
  /** Fixed form: every item. Adaptive: only the item to answer now. */
  items: z.array(questionSchema),
  answered: z.array(z.object({ question_id: z.string().uuid(), flagged: z.boolean() })),
  total: z.number().int().nullable(),
  adaptive: z.object({ min: z.number().int(), max: z.number().int(), readiness: z.number().min(0).max(1) }).nullable(),
});
export type MockExam = z.infer<typeof mockSchema>;

export const mockAnswerRequestSchema = z.object({
  question_id: z.string().uuid(),
  response: itemResponseSchema.nullable(),
  seconds: z.number().int().min(0).max(6 * 3600),
  flagged: z.boolean().default(false),
});
export const mockAnswerResponseSchema = z.object({ next_item: questionSchema.nullable(), done: z.boolean(), answered: z.number().int(), readiness: z.number().nullable() });

export const mockResultsSchema = z.object({
  id: z.string().uuid(),
  size: mockSizeSchema,
  mode: z.enum(["fixed", "adaptive"]),
  exam_name: z.string(),
  score_percent: z.number().int(),
  answered: z.number().int(),
  total: z.number().int(),
  duration_seconds: z.number().int(),
  categories: z.array(z.object({ key: z.string(), name: z.string(), score_percent: z.number().int().nullable(), answered: z.number().int() })),
  items: z.array(z.object({ question: questionSchema, response: itemResponseSchema.nullable(), score: z.number(), correct: z.boolean(), flagged: z.boolean() })),
  fix_list: z.array(z.string()).nullable(),
  adaptive_result: z.enum(["above", "below", "undetermined"]).nullable(),
});
export type MockResults = z.infer<typeof mockResultsSchema>;
